import CloseOutlined from '@mui/icons-material/CloseOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import { alpha, Box, Drawer } from '@mui/material';
import { Button, IconButton, Stack, Text, useColors } from '@si/components';
import { useTranslate } from '@si/utils';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { GuidedTour } from './GuidedTour';
import { learningContent as content } from './learningContent';
import {
  DATA_RELEASE_ID,
  DATA_TOUR_ID,
  markReleaseRead,
  markTourCompleted,
} from './learningProgress';

interface Props {
  onClose: () => void;
  onProgressChange: () => void;
  open: boolean;
  userId?: number;
}

export function LearningCenter({ onClose, onProgressChange, open, userId }: Props) {
  const { border, primary, surface } = useColors();
  const { t, translate } = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const [tourStep, setTourStep] = useState<number | null>(null);
  const [tourProjectId, setTourProjectId] = useState('');
  const currentProjectId = location.pathname.match(/^\/projects\/([^/]+)/)?.[1] ?? '';

  useEffect(() => {
    if (!open) return;
    markReleaseRead(userId, DATA_RELEASE_ID);
    onProgressChange();
  }, [onProgressChange, open, userId]);

  const closeDrawer = () => onClose();
  const goToProjectSelection = () => {
    closeDrawer();
    navigate('/projects');
  };
  const startGuide = () => {
    if (!currentProjectId) {
      goToProjectSelection();
      return;
    }

    setTourProjectId(currentProjectId);
    setTourStep(0);
    closeDrawer();
  };
  const closeTour = () => {
    setTourStep(null);
  };
  const completeTour = () => {
    markTourCompleted(userId, `${DATA_TOUR_ID}:full-tour`);
    onProgressChange();
    closeTour();
  };

  return (
    <>
      <Drawer
        anchor="right"
        onClose={closeDrawer}
        open={open}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: surface,
              backgroundImage: 'none',
              maxWidth: '100%',
              width: { xs: '100%', sm: '27rem' },
            },
          },
        }}
      >
        <Stack gap="1.25rem" p={{ xs: '1rem', sm: '1.5rem' }}>
          <Stack gap="0.375rem">
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <Stack alignItems="center" direction="row" gap="0.5rem" minWidth={0}>
                <SchoolOutlined sx={{ color: primary }} />
                <Box component="h2" sx={{ fontSize: '1.35rem', margin: 0 }}>
                  {translate(content.learningCenter)}
                </Box>
              </Stack>
              <IconButton
                ariaLabel={translate(content.closeLearningCenter)}
                icon={CloseOutlined}
                iconSize="small"
                onClick={closeDrawer}
                size="small"
              />
            </Stack>
            <Text color="darkGrey" fontSize="0.875rem">
              {translate(content.introduction)}
            </Text>
          </Stack>

          <Stack gap="0.75rem">
            <Box
              component="article"
              sx={{
                backgroundColor: alpha(primary, 0.09),
                border: '1px solid',
                borderColor: primary,
                borderRadius: '0.75rem',
                padding: '1rem',
              }}
            >
              <Stack alignItems="flex-start" gap="0.5rem">
                <Text bold>{translate(t.projectData)}</Text>
                <Text color="darkGrey" fontSize="0.875rem">
                  {translate(content.dataTabDescription)}
                </Text>
                <Button onClick={startGuide} size="small">
                  {translate(currentProjectId ? content.startFullTour : content.chooseProject)}
                </Button>
              </Stack>
            </Box>
          </Stack>

          <Stack gap="0.75rem">
            {[
              [t.dashboard, content.dashboardTabDescription],
              [t.installation, content.installationTabDescription],
              [t.documentation, content.documentationTabDescription],
              [t.settings, content.settingsTabDescription],
            ].map(([title, description]) => (
              <Box
                component="article"
                key={title.en}
                sx={{
                  border: `1px solid ${border}`,
                  borderRadius: '0.75rem',
                  padding: '1rem',
                }}
              >
                <Stack gap="0.375rem">
                  <Text bold>{translate(title)}</Text>
                  <Text color="darkGrey" fontSize="0.875rem">
                    {translate(description)}
                  </Text>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Drawer>

      {tourStep != null && tourProjectId && (
        <GuidedTour
          onClose={closeTour}
          onComplete={completeTour}
          onStepChange={setTourStep}
          projectId={tourProjectId}
          step={tourStep}
        />
      )}
    </>
  );
}
